import type { ResourceProps } from '@refinedev/core'
import {
  Building2,
  Gamepad2,
  History,
  LayoutDashboard,
  MapPin,
  MonitorSmartphone,
  Settings,
  Shield,
  Users,
} from 'lucide-react'
import { PLATFORM_SETTINGS_PATH } from '~/pages/platformSettings/paths'
import {
  DASHBOARD_RESOURCE,
  SETTINGS_RESOURCE,
} from '~/providers/accessControl'
import { PLATFORM_SETTINGS_RESOURCE } from '~/providers/dataProvider'

/**
 * Admin resources, one per CRUD group of the admin contract. The names are the
 * data provider's resource keys (`src/providers/dataProvider.ts`), except the
 * dashboard: it reads `dashboard.*` through the client. The dashboard comes
 * first (the index route, and where `NavigateToResource` without a resource
 * goes), then tenant data; `dashboard`, `activity` (the audit log, list
 * only), `rounds` (a cabinet's rounds) and
 * `settings` (the tenant's Settings, through the `tenants` procedures) are
 * for staff and the tenant's owners/admins, `platform` the staff-only
 * section (both in `src/providers/accessControl.ts`).
 */
export const resources: ResourceProps[] = [
  {
    name: DASHBOARD_RESOURCE,
    list: '/',
    meta: { icon: <LayoutDashboard /> },
  },
  {
    name: 'locations',
    list: '/locations',
    show: '/locations/:id',
    create: '/locations/new',
    edit: '/locations/:id/edit',
    meta: { icon: <MapPin />, canDelete: true },
  },
  {
    name: 'cabinets',
    list: '/cabinets',
    show: '/cabinets/:id',
    create: '/cabinets/new',
    edit: '/cabinets/:id/edit',
    meta: { icon: <MonitorSmartphone /> },
  },
  // a cabinet's rounds: listed on the cabinet's page, so no list route and
  // no sidebar entry. The round's route makes it a page Refine knows
  // (breadcrumb, document title, Cabinets selected in the sidebar). Staff and
  // the tenant's owners/admins only, like `activity`.
  {
    name: 'rounds',
    show: '/cabinets/:cabinetId/rounds/:id',
    meta: { parent: 'cabinets', hide: true },
  },
  {
    name: 'activity',
    list: '/activity',
    meta: { icon: <History /> },
  },
  // the host tenant's own settings (`/settings`, tabs); changing them is for
  // its owners, admins read them
  {
    name: SETTINGS_RESOURCE,
    list: '/settings',
    meta: { icon: <Settings /> },
  },
  // staff-only section (tenants, users, games, platform activity, the
  // platform's settings); templates and tools join it later
  { name: 'platform', meta: { icon: <Shield /> } },
  {
    name: 'tenants',
    list: '/platform/tenants',
    show: '/platform/tenants/:id',
    create: '/platform/tenants/new',
    edit: '/platform/tenants/:id/edit',
    meta: { icon: <Building2 />, parent: 'platform' },
  },
  // every account (users are global, a member of any number of tenants):
  // read-only until staff manage them
  {
    name: 'users',
    list: '/platform/users',
    show: '/platform/users/:id',
    meta: { icon: <Users />, parent: 'platform' },
  },
  // games come with releases; staff edit a game's configurations (the show
  // page and the configuration route in app). `gameOptions` (the
  // cabinet form's picker, open to every signed-in user) is a data provider
  // resource only, left out here so it gets no sidebar entry.
  {
    name: 'games',
    list: '/platform/games',
    show: '/platform/games/:id',
    meta: { icon: <Gamepad2 />, parent: 'platform' },
  },
  // every tenant's log and the platform events (sign-ins, users, games), list only
  {
    name: 'platformActivity',
    list: '/platform/activity',
    meta: { icon: <History />, parent: 'platform' },
  },
  // the platform's defaults every tenant inherits until it sets its own: one
  // record (`platformSettings.get` / `update`), edited on its list route
  {
    name: PLATFORM_SETTINGS_RESOURCE,
    list: PLATFORM_SETTINGS_PATH,
    meta: { icon: <Settings />, parent: 'platform' },
  },
  // a game's configurations: listed on the game's page, so no list route
  // and no sidebar entry. The route of a configuration's page makes it a
  // page Refine knows (breadcrumb, document title, Platform
  // › Games selected in the sidebar). `:id` is the game, as on the game's
  // page.
  {
    name: 'gameConfigurations',
    show: '/platform/games/:id/configurations/:configurationId',
    meta: { parent: 'games', hide: true },
  },
]
